/**
 * 데이터 브라우저 행 → docparser 문서 정보 추출.
 *
 * docparser 수집 모듈(comp_earnings 등)의 행에는 변환 산출물 경로가 들어 있다:
 *   md_path      : "comp_earnings/dp_.../xxx.pdf.md"
 *   image_paths  : JSON 배열 문자열 또는 배열
 *   source_url   : 외부 CDN 원본 파일 URL
 *   source_meta  : 조회 API 원본 레코드(JSON) — 발행사 등 표시용 메타
 *
 * 컬럼 존재 여부로만 판단한다 — 모듈명을 하드코딩하지 않아, 같은 형태의 모듈이
 * 늘어나도 그대로 동작한다. (DataBrowser의 "컬럼은 응답 스키마 그대로" 원칙과 동일)
 */

/** 행에서 뽑아낸 문서 정보. mdPath가 null이면 변환 산출물이 없는 행. */
export interface DocRowInfo {
  /**
   * doc-file 조회에 쓸 수집 모듈명 (예: comp_earnings).
   * 데이터 브라우저의 "모듈"은 DB 스키마명(pblob)이라 이것과 다르다 —
   * md_path의 첫 세그먼트가 곧 모듈 디렉토리이므로 거기서 얻는다.
   */
  module: string | null;
  mdPath: string | null;
  imagePaths: string[];
  sourceUrl?: string;
  title?: string;
  publisher?: string;
  docDate?: string;
}

/** 컬럼명 → 값. 없으면 undefined. */
function pick(columns: string[], row: (string | number)[], name: string): string | undefined {
  const i = columns.indexOf(name);
  if (i < 0) return undefined;
  const v = row[i];
  if (v === null || v === undefined || v === '') return undefined;
  return String(v);
}

/**
 * JSON 문자열(또는 이미 배열)을 문자열 배열로. 파싱 실패하면 빈 배열.
 * image_paths는 DB에서 jsonb로 오지만 그리드까지 오면 문자열일 수 있다.
 */
function parseStringArray(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** source_meta(JSON 문자열)에서 표시용 필드만 얕게 꺼낸다. 실패해도 무시. */
function parseMeta(raw: string | undefined): { publisher?: string; title?: string } {
  if (!raw) return {};
  try {
    const m = JSON.parse(raw) as { publisher?: unknown; title?: unknown };
    return {
      publisher: typeof m.publisher === 'string' ? m.publisher : undefined,
      title: typeof m.title === 'string' ? m.title : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * 행이 docparser 문서인지 — md_path 컬럼이 있으면 문서 행으로 본다.
 * (값이 비어 있어도 컬럼이 있으면 문서 테이블이므로 뷰어 진입은 허용하고,
 *  뷰어가 "변환된 문서 없음"을 안내한다.)
 */
export function isDocRow(columns: string[]): boolean {
  return columns.includes('md_path');
}

/** 선택 행에서 문서 뷰어에 필요한 값들을 추출. */
export function extractDocRow(columns: string[], row: (string | number)[]): DocRowInfo {
  const meta = parseMeta(pick(columns, row, 'source_meta'));
  const mdPath = pick(columns, row, 'md_path') ?? null;
  // 경로가 `<module>/<batchId>/...` 형태 → 첫 세그먼트가 수집 모듈명.
  const imagePaths = parseStringArray(pick(columns, row, 'image_paths'));
  const anyPath = mdPath ?? imagePaths[0] ?? null;
  const module = anyPath && anyPath.includes('/') ? anyPath.split('/')[0] : null;
  return {
    module,
    mdPath,
    imagePaths,
    sourceUrl: pick(columns, row, 'source_url'),
    // title 컬럼이 우선, 없으면 source_meta.title
    title: pick(columns, row, 'title') ?? meta.title,
    publisher: meta.publisher,
    docDate: pick(columns, row, 'doc_date'),
  };
}
